import booleanPointInPolygon from "@turf/boolean-point-in-polygon"
import type { BBox, Feature, Point, Polygon, Position } from "geojson"
import { useCallback, useEffect, useRef, useState } from "react"
import type { MapRef } from "react-map-gl/mapbox"
import { useBioMeshUIState } from "./BioMeshUIStateContext"
import { getCoordinates } from "./getCoordinates"
import type { ClusterOrPoint, NodeFeature } from "./useNodeClusters"
import { isCluster } from "./useNodeClusters"

/** GeoJSON representing the drawn lasso polygon (null while not drawn). */
export type LassoPolygon = Feature<Polygon> | null

const LASSO_SOURCE_ID = "lasso-polygon"

function makePolygonGeoJSON(ring: Position[]): Feature<Polygon> {
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [ring] },
  }
}

const EMPTY_GEOJSON = makePolygonGeoJSON([])

/** Shift a longitude to be within ±180° of a reference longitude. */
function unwrapLng(lng: number, refLng: number): number {
  let adjusted = lng
  while (adjusted - refLng > 180) adjusted -= 360
  while (adjusted - refLng < -180) adjusted += 360
  return adjusted
}

/**
 * Hook that implements freeform lasso drawing on a Mapbox GL map.
 *
 * When `isSelecting` is true and the user clicks+drags on the map,
 * a freeform polygon is drawn. On mouse-up, nodes inside the polygon
 * are identified and written to `selectedNodeIds` in the UI state context.
 *
 * During drawing, the Mapbox GeoJSON source is updated directly (bypassing
 * React state) so that re-renders of the component tree are avoided on
 * every mousemove.  React state is only written on the final polygon.
 */
export function useLassoSelect(
  mapRef: React.RefObject<MapRef | null>,
  getClusters: (bounds: BBox, zoom: number) => ClusterOrPoint[],
  getLeaves: (clusterId: number, limit?: number) => NodeFeature[]
) {
  const { isSelecting, setSelectedNodeIds } = useBioMeshUIState()
  const [lassoGeoJSON, setLassoGeoJSON] = useState<LassoPolygon>(null)
  const drawingRef = useRef(false)
  const coordsRef = useRef<Position[]>([])
  const rafRef = useRef<number | null>(null)

  /** Push the current coordinate buffer to the Mapbox source imperatively. */
  const flushToSource = useCallback(() => {
    rafRef.current = null
    const map = mapRef.current?.getMap()
    if (!map) return
    const src = map.getSource(LASSO_SOURCE_ID)
    if (src?.type !== "geojson") return

    const ring = coordsRef.current
    const first = ring[0]
    if (!first || ring.length < 2) return
    src.setData(makePolygonGeoJSON([...ring, first]))
  }, [mapRef])

  /** Schedule a single RAF to push coords to the source. */
  const scheduleFlush = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(flushToSource)
  }, [flushToSource])

  const finishLasso = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    drawingRef.current = false
    map.dragPan.enable()

    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    const ring = coordsRef.current
    if (ring.length < 3) {
      setLassoGeoJSON(null)
      coordsRef.current = []
      return
    }

    const first = ring[0]
    if (!first) {
      setLassoGeoJSON(null)
      coordsRef.current = []
      return
    }
    const closed: Position[] = [...ring, first]
    const polygon = makePolygonGeoJSON(closed)
    setLassoGeoJSON(polygon)

    // Get the visible clusters/points at the current zoom level
    const bounds = map.getBounds()
    if (!bounds) return
    const zoom = map.getZoom()
    const bbox: BBox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ]
    const visible = getClusters(bbox, zoom)

    // Hit-test visible clusters/points against the polygon.
    // Use the polygon's average longitude as a reference so that points
    // on the other side of the anti-meridian are unwrapped into the
    // same coordinate space.
    const refLng = ring.reduce((sum, c) => sum + (c[0] ?? 0), 0) / ring.length
    const selected = new Set<string>()
    for (const item of visible) {
      const coords = getCoordinates(item.geometry)
      if (!coords) continue
      const [lng, lat] = coords
      const testPt: Feature<Point> = {
        type: "Feature",
        properties: {},
        geometry: {
          type: "Point",
          coordinates: [unwrapLng(lng, refLng), lat],
        },
      }
      if (!booleanPointInPolygon(testPt, polygon)) continue

      if (isCluster(item)) {
        // Expand cluster → include all leaf platform_ids
        const leaves = getLeaves(item.properties.cluster_id, Infinity)
        for (const leaf of leaves) {
          selected.add(leaf.properties.platform_id)
        }
      } else {
        selected.add(item.properties.platform_id)
      }
    }
    setSelectedNodeIds(selected)
    coordsRef.current = []
  }, [mapRef, getClusters, getLeaves, setSelectedNodeIds])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    if (!isSelecting) {
      map.dragPan.enable()
      return
    }

    const canvas = map.getCanvas()

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return

      drawingRef.current = true
      coordsRef.current = []
      map.dragPan.disable()

      const lngLat = map.unproject([e.offsetX, e.offsetY])
      coordsRef.current.push([lngLat.lng, lngLat.lat])
      // Show empty source immediately so Mapbox layers are active
      setLassoGeoJSON(EMPTY_GEOJSON)
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!drawingRef.current) return

      const lngLat = map.unproject([e.offsetX, e.offsetY])
      coordsRef.current.push([lngLat.lng, lngLat.lat])

      // Throttle visual updates to one per animation frame
      scheduleFlush()
    }

    const onMouseUp = () => {
      if (!drawingRef.current) return
      finishLasso()
    }

    canvas.addEventListener("mousedown", onMouseDown)
    canvas.addEventListener("mousemove", onMouseMove)
    canvas.addEventListener("mouseup", onMouseUp)

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown)
      canvas.removeEventListener("mousemove", onMouseMove)
      canvas.removeEventListener("mouseup", onMouseUp)
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      if (drawingRef.current) {
        drawingRef.current = false
        map.dragPan.enable()
      }
    }
  }, [mapRef, isSelecting, finishLasso, scheduleFlush])

  const clearLasso = useCallback(() => {
    setLassoGeoJSON(null)
    const map = mapRef.current?.getMap()
    if (map) {
      const src = map.getSource(LASSO_SOURCE_ID)
      if (src?.type === "geojson") {
        src.setData(EMPTY_GEOJSON)
      }
    }
  }, [mapRef])

  return { lassoGeoJSON, clearLasso }
}
